import { useTranslations } from "use-intl";

import { PlayIcon } from "@/components/appIcons";
import { ArtistLinks } from "@/components/artist-links";
import { EqualizerIcon } from "@/components/equalizer-icon";
import { cn } from "@/lib/utils";
import type { Medium } from "@garimpa/lib/musicTypes";

type Track = Medium["tracks"][number];

/** Row grid shared by every tracklist row: number · play slot · title · duration. */
export const TRACK_ROW_GRID_CLASS =
  "grid grid-cols-[2.5rem_2.25rem_minmax(0,1fr)_auto] items-center gap-x-2 sm:grid-cols-[2.5rem_1.75rem_minmax(0,1fr)_auto]";

function durationIso(duration: string | null): string | undefined {
  const match = duration?.match(/^(?:(\d+):)?(\d+):(\d{2})$/);
  if (!match) return undefined;
  return match[1] ? `PT${match[1]}H${match[2]}M${match[3]}S` : `PT${match[2]}M${match[3]}S`;
}

function TrackDuration({ duration, className }: { duration: string | null; className: string }) {
  const display = duration ?? "--:--";
  const dateTime = durationIso(duration);

  return dateTime ? (
    <time dateTime={dateTime} className={className}>
      {display}
    </time>
  ) : (
    <span className={className}>{display}</span>
  );
}

function TrackRowPlayButton({
  isPlaying,
  onPlay,
  title,
  youtubeId,
}: {
  isPlaying: boolean;
  onPlay: (youtubeId: string) => void;
  title: string;
  youtubeId: string | null;
}) {
  const t = useTranslations("release");
  // Rows without a video keep an empty slot so titles stay aligned.
  if (!youtubeId) return <span aria-hidden="true" className="size-9 sm:size-7" />;

  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onPlay(youtubeId);
      }}
      aria-label={t("playTrack", { title })}
      className={cn(
        // A 36px disc on phones, 28px from `sm`; the ::after inset keeps the hit area at 44px.
        "relative flex size-9 items-center justify-center rounded-full text-primary transition-colors after:absolute after:-inset-1 after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:size-7 sm:after:-inset-2",
        isPlaying ? "bg-primary/25" : "bg-primary/15 hover:bg-primary/25",
      )}
    >
      {isPlaying ? (
        <EqualizerIcon className="scale-75 sm:scale-[0.6]" />
      ) : (
        <PlayIcon className="text-sm leading-none sm:text-xs" decorative />
      )}
    </button>
  );
}

export function TrackRowContent({
  isPlaying,
  onPlay,
  track,
  youtubeId,
}: {
  isPlaying: boolean;
  onPlay: (youtubeId: string) => void;
  track: Track;
  youtubeId: string | null;
}) {
  return (
    <>
      <span className="font-mono text-[0.8125rem] text-muted-foreground">{track.number}</span>
      <TrackRowPlayButton
        isPlaying={isPlaying}
        onPlay={onPlay}
        title={track.title}
        youtubeId={youtubeId}
      />
      <p
        className={cn(
          "min-w-0 text-[0.9375rem] font-semibold leading-snug [overflow-wrap:anywhere]",
          isPlaying && "text-primary",
        )}
      >
        {track.title}
        {track.featuring.length > 0 && (
          <span className="font-normal text-muted-foreground">
            {" (feat. "}
            <ArtistLinks
              appearance="text"
              artists={track.featuring}
              className="text-muted-foreground"
            />
            {")"}
          </span>
        )}
      </p>
      <TrackDuration
        duration={track.duration}
        className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground"
      />
    </>
  );
}
